import { ChevronDownIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	Checkbox,
	CheckboxGroup,
	FormControl,
	FormLabel,
	HStack,
	Heading,
	Input,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Select,
	Skeleton,
	Switch,
	Stack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Textarea,
	Tooltip,
	VStack
} from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { HiChevronLeft } from "react-icons/hi";
import { generatePath, useNavigate, useParams } from "react-router-dom";

// Components
import DataCardDisplay, { DataItem } from "../../../shared/DataDisplay/DataDisplay";
import DataCardDisplayDocs from "../../../shared/DataDisplayDocs/DataCardDisplayDocs";
import DataCardDisplayStatement from "../../../shared/DataDisplayStatement";
import LoadingSpinner from "../../../shared/LoadingSpinner/LoadingSpinner";
import CustomModal from "../../../shared/Modals/CustomModal";
import Notes from "../../../shared/Notes";
import SectionHeader from "../../../shared/SectionHeader/SectionHeader";
import LayoutWrapper from "../../../shared/layout";
import { Page } from "../../../shared/layout/index.styles";
import ClaimPaymentsTable from "../payments/ClaimPaymentsTable";
import ClaimReportsTable from "../reports/ClaimReportsTable";
import ActivityPane from "./ActivityPane";
import Checklist from "./Checklist";
import ServiceBillingEditModal from "./ServiceBillingEditModal";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import usePermission from "../../../hooks/usePermission";

// Providers
import { useClaimDetails } from "../../../context-providers/claims-details-provider";
import { useClaim } from "../../../context-providers/claims-provider";

// Utils
import { RouterPathMapping } from "../../../Router";
import { ClaimStatus, EnvKeys, UserRole } from "../../../utils/consts";
import {
	createExternalLinkFromEnv,
	doesUserHaveRoles,
	transformClaimToDisplayStatementData
} from "../../../utils/helpers";
import { User } from "../../../utils/types";
import { ClaimsStatusReason } from "../../../utils/api/protos/messages/claims.message_pb";
import DetailField from "../../../shared/DetailElement/DetailField";
import AttorneyActiveBadge from "../../../shared/AttorneyActiveBadge/AttorneyActiveBadge";
import BasicInfoEditModal from "./BasicInfoEditModal";

interface ClaimsDetailProps {}

const ChecklistStatuses: Array<ClaimStatus> = [
	ClaimStatus.InReview,
	ClaimStatus.Approved,
	ClaimStatus.Denied,
	ClaimStatus.ClosedNotPaid,
	ClaimStatus.PendingProvider,
	ClaimStatus.PendingVerification,
	ClaimStatus.PendingInternalReview
];

const ClaimsDetail: FunctionComponent<ClaimsDetailProps> = (): ReactElement => {
	const { id } = useParams();
	const {
		claim,
		claimCase,
		benefits,
		analysts,
		isPremiumPaid,
		isLoading,
		handleFetchClaim,
		handleAssignAnalyst,
		handleApproveClaim,
		handleDenyClaim,
		handleInReview,
		handleCancelClaim,
		handleUncancelClaim,
		handleQueueClaimForPayment,
		handleClosePaidClaim,
		handleCloseNotPaidClaim,
		handleCloseNotPaidGoodwillClaim,
		handleDuplicateClaim,
		handlePendingProvider,
		handlePendingVerification,
		handlePendingInternalReview,
		getServiceLabel,
		handleIsPremiumPaid,
		handleGeneratePdf,
		handleHoldForAuditChange,
		handleFetchActivities
	} = useClaimDetails();
	const { handleFetchClaim: handleFetchClaimAlternate } = useClaim();
	const navigate = useNavigate();
	const [isInitialLoading, setInitialLoading] = useState(true);
	const [isApproveClaimModalOpen, setIsApproveClaimModalOpen] = useState<boolean>(false);
	const [cancelReason, setCancelReason] = useState<string>("");
	const [denyReason, setDenyReason] = useState<string>("");
	const [pendingProviderReasons, setPendingProviderReasons] = useState<(string | number)[]>([]);
	const [pendingProviderOtherReason, setPendingProviderOtherReason] = useState<string | undefined>(undefined);
	const [sendPendingProviderEmail, setSendPendingProviderEmail] = useState<boolean>(true);
	const [isCancelClaimModalOpen, setIsCancelClaimModalOpen] = useState<boolean>(false);
	const [isUncancelClaimModalOpen, setIsUncancelClaimModalOpen] = useState<boolean>(false);
	const [isDenyClaimModalOpen, setIsDenyClaimModalOpen] = useState<boolean>(false);
	const [isInReviewModalOpen, setIsInReviewModalOpen] = useState<boolean>(false);
	const [isQueueClaimForPaymentModalOpen, setIsQueueClaimForPaymentModalOpen] = useState<boolean>(false);
	const [isClosePaidClaimModalOpen, setIsClosePaidClaimModalOpen] = useState<boolean>(false);
	const [isCloseNotPaidClaimModalOpen, setIsCloseNotPaidClaimModalOpen] = useState<boolean>(false);
	const [isCloseNotPaidGoodwillModalOpen, setIsCloseNotPaidGoodwillModalOpen] = useState<boolean>(false);
	const [isDuplicateClaimModalOpen, setIsDuplicateClaimModalOpen] = useState<boolean>(false);
	const [isPendingProviderModalOpen, setIsPendingProviderModalOpen] = useState<boolean>(false);
	const [isPendingVerificationModalOpen, setIsPendingVerificationModalOpen] = useState<boolean>(false);
	const [isPendingInternalReviewModalOpen, setIsPendingInternalReviewModalOpen] = useState<boolean>(false);
	const [isServiceBillingEditModalOpen, setIsServiceBillingEditModalOpen] = useState<boolean>(false);
	const [isBasicInfoEditModalOpen, setIsBasicInfoEditModalOpen] = useState<boolean>(false);
	const docEditButtonRef = useRef<HTMLButtonElement>(null);
	const { getUser } = useAuth();
	const { roles, hasGeneralManagerAccess, hasGeneralUpdateAccess, hasGeneralAdminAccess, isProvider } = usePermission();
	const resetModals = (refreshClaimContext = false): void => {
		// A parameter to refresh the claim context, which is needed for editing documents
		if (refreshClaimContext && claim?.claimId) {
			handleFetchClaimAlternate(claim?.claimId);
		}
		setIsApproveClaimModalOpen(false);
		setIsCancelClaimModalOpen(false);
		setIsUncancelClaimModalOpen(false);
		setIsDenyClaimModalOpen(false);
		setIsInReviewModalOpen(false);
		setIsQueueClaimForPaymentModalOpen(false);
		setIsClosePaidClaimModalOpen(false);
		setIsCloseNotPaidClaimModalOpen(false);
		setIsCloseNotPaidGoodwillModalOpen(false);
		setIsDuplicateClaimModalOpen(false);
		setIsPendingProviderModalOpen(false);
		setIsPendingVerificationModalOpen(false);
		setIsPendingInternalReviewModalOpen(false);
		setIsServiceBillingEditModalOpen(false);
		setIsBasicInfoEditModalOpen(false);
		setCancelReason("");
		setDenyReason("");
		setPendingProviderReasons([]);
		setPendingProviderOtherReason(undefined);
		setSendPendingProviderEmail(true);
	};

	const [canEditAssignedAnalyst, setCanEditAssignedAnalyst] = useState<boolean>(false);

	const getMenuList = (): Array<ReactElement> => {
		const menuItems: Array<ReactElement> = [];
		if (claim) {
			if (claim.isHeldForAudit) {
				return [
					<Box key="audit-hold" padding="6px 8px">
						Status changes disabled during audit
					</Box>
				];
			}

			let approvalValidationError = "";
			if (!claim.checklistComplete) {
				approvalValidationError = "The checklist must be completed before approving this claim";
			} else if (!claim.cleanDate) {
				approvalValidationError = "A clean date is required for approving this claim";
			} else if (!claim.receivedOn) {
				approvalValidationError = "A received on date is required for approving this claim";
			}

			if (claim.status === ClaimStatus.InReview) {
				menuItems.push(
					<Tooltip key={ClaimStatus.Approved} label={approvalValidationError}>
						<MenuItem
							key={ClaimStatus.Approved}
							onClick={() => setIsApproveClaimModalOpen(true)}
							isDisabled={!!approvalValidationError}>
							{ClaimStatus.Approved}
						</MenuItem>
					</Tooltip>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.Denied} onClick={() => setIsDenyClaimModalOpen(true)}>
						Deny
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaid} onClick={() => setIsCloseNotPaidClaimModalOpen(true)}>
						{ClaimStatus.ClosedNotPaid}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingProvider} onClick={() => setIsPendingProviderModalOpen(true)}>
						{ClaimStatus.PendingProvider}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingVerification} onClick={() => setIsPendingVerificationModalOpen(true)}>
						{ClaimStatus.PendingVerification}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingInternalReview} onClick={() => setIsPendingInternalReviewModalOpen(true)}>
						{ClaimStatus.PendingInternalReview}
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.Approved) {
				menuItems.push(
					<MenuItem key={ClaimStatus.InReview} onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaid} onClick={() => setIsCloseNotPaidClaimModalOpen(true)}>
						{ClaimStatus.ClosedNotPaid}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingProvider} onClick={() => setIsPendingProviderModalOpen(true)}>
						{ClaimStatus.PendingProvider}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingVerification} onClick={() => setIsPendingVerificationModalOpen(true)}>
						{ClaimStatus.PendingVerification}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingInternalReview} onClick={() => setIsPendingInternalReviewModalOpen(true)}>
						{ClaimStatus.PendingInternalReview}
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.QueuedForPayment) {
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedPaid} onClick={() => setIsClosePaidClaimModalOpen(true)}>
						Close As Paid
					</MenuItem>
				);
				if (doesUserHaveRoles(getUser(), [UserRole.ClaimsManager])) {
					menuItems.push(
						<MenuItem key={ClaimStatus.Duplicate} onClick={() => setIsDuplicateClaimModalOpen(true)}>
							{ClaimStatus.Duplicate}
						</MenuItem>
					);
				}
			}
			// Draft and Submitted claims can be cancelled by managers, admins can only cancel Draft claims
			if (
				([ClaimStatus.Draft, ClaimStatus.Submitted].includes(claim.status) && hasGeneralManagerAccess) ||
				(claim.status === ClaimStatus.Draft && roles.includes(UserRole.ClaimsAdmin))
			) {
				menuItems.push(
					<MenuItem key={ClaimStatus.Cancelled} onClick={() => setIsCancelClaimModalOpen(true)}>
						Cancel
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.Cancelled && hasGeneralManagerAccess) {
				menuItems.push(
					<MenuItem key={ClaimStatus.Cancelled} onClick={() => setIsUncancelClaimModalOpen(true)}>
						Un-Cancel
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.Denied) {
				menuItems.push(
					<Tooltip key="approve" label={approvalValidationError}>
						<MenuItem
							key={ClaimStatus.Approved}
							onClick={() => setIsApproveClaimModalOpen(true)}
							isDisabled={!!approvalValidationError}>
							{ClaimStatus.Approved}
						</MenuItem>
					</Tooltip>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.InReview} onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaid} onClick={() => setIsCloseNotPaidClaimModalOpen(true)}>
						{ClaimStatus.ClosedNotPaid}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingProvider} onClick={() => setIsPendingProviderModalOpen(true)}>
						{ClaimStatus.PendingProvider}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingVerification} onClick={() => setIsPendingVerificationModalOpen(true)}>
						{ClaimStatus.PendingVerification}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingInternalReview} onClick={() => setIsPendingInternalReviewModalOpen(true)}>
						{ClaimStatus.PendingInternalReview}
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.ClosedNotPaid) {
				menuItems.push(
					<Tooltip key="approve" label={approvalValidationError}>
						<MenuItem
							key={ClaimStatus.Approved}
							onClick={() => setIsApproveClaimModalOpen(true)}
							isDisabled={!!approvalValidationError}>
							{ClaimStatus.Approved}
						</MenuItem>
					</Tooltip>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.InReview} onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingProvider} onClick={() => setIsPendingProviderModalOpen(true)}>
						{ClaimStatus.PendingProvider}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingVerification} onClick={() => setIsPendingVerificationModalOpen(true)}>
						{ClaimStatus.PendingVerification}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingInternalReview} onClick={() => setIsPendingInternalReviewModalOpen(true)}>
						{ClaimStatus.PendingInternalReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaidGoodwill} onClick={() => setIsCloseNotPaidGoodwillModalOpen(true)}>
						Mark as Goodwill Payment
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.PendingProvider) {
				menuItems.push(
					<Tooltip key="approve" label={approvalValidationError}>
						<MenuItem
							key={ClaimStatus.Approved}
							onClick={() => setIsApproveClaimModalOpen(true)}
							isDisabled={!!approvalValidationError}>
							{ClaimStatus.Approved}
						</MenuItem>
					</Tooltip>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.Denied} onClick={() => setIsDenyClaimModalOpen(true)}>
						{ClaimStatus.Denied}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.InReview} onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaid} onClick={() => setIsCloseNotPaidClaimModalOpen(true)}>
						{ClaimStatus.ClosedNotPaid}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingVerification} onClick={() => setIsPendingVerificationModalOpen(true)}>
						{ClaimStatus.PendingVerification}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingInternalReview} onClick={() => setIsPendingInternalReviewModalOpen(true)}>
						{ClaimStatus.PendingInternalReview}
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.PendingVerification) {
				menuItems.push(
					<Tooltip key="approve" label={approvalValidationError}>
						<MenuItem
							key={ClaimStatus.Approved}
							onClick={() => setIsApproveClaimModalOpen(true)}
							isDisabled={!!approvalValidationError}>
							{ClaimStatus.Approved}
						</MenuItem>
					</Tooltip>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.Denied} onClick={() => setIsDenyClaimModalOpen(true)}>
						{ClaimStatus.Denied}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.InReview} onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaid} onClick={() => setIsCloseNotPaidClaimModalOpen(true)}>
						{ClaimStatus.ClosedNotPaid}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingProvider} onClick={() => setIsPendingProviderModalOpen(true)}>
						{ClaimStatus.PendingProvider}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingInternalReview} onClick={() => setIsPendingInternalReviewModalOpen(true)}>
						{ClaimStatus.PendingInternalReview}
					</MenuItem>
				);
			}
			if (claim.status === ClaimStatus.PendingInternalReview) {
				menuItems.push(
					<Tooltip key="approve" label={approvalValidationError}>
						<MenuItem
							key={ClaimStatus.Approved}
							onClick={() => setIsApproveClaimModalOpen(true)}
							isDisabled={!!approvalValidationError}>
							{ClaimStatus.Approved}
						</MenuItem>
					</Tooltip>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.Denied} onClick={() => setIsDenyClaimModalOpen(true)}>
						{ClaimStatus.Denied}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.InReview} onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.ClosedNotPaid} onClick={() => setIsCloseNotPaidClaimModalOpen(true)}>
						{ClaimStatus.ClosedNotPaid}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key={ClaimStatus.PendingProvider} onClick={() => setIsPendingProviderModalOpen(true)}>
						{ClaimStatus.PendingProvider}
					</MenuItem>
				);
				menuItems.push(
					<MenuItem key="pending-verification" onClick={() => setIsPendingVerificationModalOpen(true)}>
						{ClaimStatus.PendingVerification}
					</MenuItem>
				);
			}
			// Duplicate status: can be set to In-Review
			if (claim.status === ClaimStatus.Duplicate) {
				menuItems.push(
					<MenuItem key="in-review" onClick={() => setIsInReviewModalOpen(true)}>
						{ClaimStatus.InReview}
					</MenuItem>
				);
			}
			// Mark as Duplicate: available for all statuses except Approved, Queued for Payment, Closed - Paid, Duplicate, Cancelled, Draft, and Submitted
			if (
				claim.status !== ClaimStatus.Approved &&
				claim.status !== ClaimStatus.QueuedForPayment &&
				claim.status !== ClaimStatus.ClosedPaid &&
				claim.status !== ClaimStatus.Duplicate &&
				claim.status !== ClaimStatus.Cancelled &&
				claim.status !== ClaimStatus.Draft &&
				claim.status !== ClaimStatus.Submitted
			) {
				menuItems.push(
					<MenuItem key="mark-duplicate" onClick={() => setIsDuplicateClaimModalOpen(true)}>
						Mark as Duplicate
					</MenuItem>
				);
			}
			if (menuItems.length === 0) {
				menuItems.push(
					<Box key="no-actions" padding="6px 8px">
						No actions available
					</Box>
				);
			}
		}
		return menuItems;
	};

	const getAuditBadge = (): ReactElement => {
		if (!claim?.isHeldForAudit) return <></>;
		return (
			<Tooltip label="This claim is held for audit">
				<Badge ml="1" colorScheme="purple" style={{ verticalAlign: "initial" }}>
					Audit
				</Badge>
			</Tooltip>
		);
	};

	const getPaidBadge = (): ReactElement => {
		if (typeof isPremiumPaid === "undefined") {
			return <></>;
		} else if (isPremiumPaid) {
			return (
				<Tooltip label="The Premium for this Claim has been Paid">
					<Badge ml="1" colorScheme="green" style={{ verticalAlign: "initial" }}>
						Premium Paid
					</Badge>
				</Tooltip>
			);
		} else {
			return (
				<Tooltip label="The Premium for this Claim has not been Paid">
					<Badge ml="1" colorScheme="red" style={{ verticalAlign: "initial" }}>
						Premium Not Paid
					</Badge>
				</Tooltip>
			);
		}
	};

	const handleAuditHoldToggle = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
		await handleHoldForAuditChange(event.target.checked);
		if (claim?.claimId) {
			await handleFetchClaimAlternate(claim.claimId);
		}
	};

	const getClaimInformation = (): Array<DataItem> => {
		let claimInfo: Array<DataItem> = [
			{
				title: "PASS Case",
				value: `#${claim?.caseId}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.PassUrl, claim?.caseId || "")
			},
			{
				title: "LAMP Case",
				value: `#${claimCase?.getLampcaseid()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.LampCaseUrl, claimCase?.getLampcaseid() || "")
			},
			{
				title: "Member",
				value: `${claim?.member?.getName()} #${claim?.member?.getId()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.MemberPassUrl, claim?.member?.getId() || "")
			},
			{
				title: "Attorney",
				value: `${claim?.atty?.getName()} #${claim?.atty?.getId()}`,
				isurl: true,
				url: createExternalLinkFromEnv(EnvKeys.LampAttorneyUrl, claim?.atty?.getId() || ""),
				childElements: <AttorneyActiveBadge active={claim?.attyActive} />
			},
			{
				title: "Date of Initial Consultation",
				value: claim ? `${new Date(claim.dateOfInitialConsultation).toLocaleDateString()}` : "",
				childElements: getPaidBadge()
			},
			{
				title: "Final Date of Service",
				value: claim ? new Date(claim.finalDateOfService).toLocaleDateString() : ""
			},
			{
				title: "Legal Jurisdiction",
				value: claim && claim.legalMatterState ? claim.legalMatterState.getName() : ""
			},
			{
				title: "Received On",
				value: claim?.receivedOn?.toLocaleDateString() ?? ""
			},
			{
				title: "Clean Date",
				value: claim?.cleanDate ? new Date(claim.cleanDate).toLocaleDateString() : ""
			}
		];

		if (!doesUserHaveRoles(getUser(), [UserRole.Provider])) {
			claimInfo.push({
				title: "Due Date",
				value: claim?.dueDate ? new Date(claim.dueDate).toLocaleDateString() : ""
			});
		}

		claimInfo.push(
			{
				title: "Submitted",
				value: claim?.submittedAt ? new Date(claim.submittedAt).toLocaleDateString() : ""
			},
			{
				title: "Approved",
				value: claim?.approvedAt ? new Date(claim.approvedAt).toLocaleDateString() : ""
			}
		);

		return claimInfo;
	};

	useEffect(() => {
		if (id) {
			const parsedId = parseInt(id);
			handleFetchClaim(parsedId).finally(() => setInitialLoading(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (claim && claim.claimId) {
			handleIsPremiumPaid();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claim]);

	useEffect(() => {
		// Determine if the user can edit based on roles and claim status
		const currentUser: User | undefined = getUser();

		if (currentUser) {
			const userCanEditAssignedAnalyst =
				hasGeneralManagerAccess ||
				(Object.keys(claim?.assignedAnalyst ?? {}).length &&
					currentUser.id === claim?.assignedAnalyst?.getId() &&
					hasGeneralUpdateAccess);
			setCanEditAssignedAnalyst(!!userCanEditAssignedAnalyst);
		}
	}, [claim]);

	const claimDisplayStatementData = useMemo(() => {
		if (claim) {
			return transformClaimToDisplayStatementData(claim, getServiceLabel);
		}
	}, [claim, benefits]);

	const getAnalystOptions = (): ReactElement[] => {
		const options: ReactElement[] = [];

		analysts?.forEach((analyst, idx) => {
			if (analyst && Object.keys(analyst).length > 0) {
				options.push(
					<option
						key={analyst?.getId() + idx}
						value={analyst?.getId()}
						disabled={
							Object.keys(claim?.assignedAnalyst ?? {}).length > 0 &&
							analyst?.getId() === claim?.assignedAnalyst?.getId()
						}>
						{analyst?.getName()}
					</option>
				);
			}
		});

		return options;
	};

	return (
		<LayoutWrapper>
			<HStack height="100%" overflow="hidden" gap="0">
				{isInitialLoading && <LoadingSpinner />}
				<CustomModal
					isOpen={isApproveClaimModalOpen}
					size="3xl"
					message={`Are you sure you want to approve this claim for $${claimDisplayStatementData?.data.summary.totalNetworkCost}?`}
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleApproveClaim().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					title="Deny Claim"
					isOpen={isDenyClaimModalOpen}
					size="3xl"
					content={
						<Textarea
							placeholder="Deny Reason..."
							value={denyReason}
							onChange={(event) => setDenyReason(event.target.value)}
							borderColor="gray.200"
						/>
					}
					continueText="Deny"
					continueLoading={isLoading}
					onContinue={() => handleDenyClaim(denyReason.trim()).then(() => resetModals(true))}
					continueDisabled={denyReason.trim().length === 0}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isInReviewModalOpen}
					size="3xl"
					message="Are you sure you want to queue this claim as in review?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleInReview().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					title="Cancel Claim"
					isOpen={isCancelClaimModalOpen}
					size="3xl"
					content={
						<Textarea
							placeholder="Cancellation Reason..."
							value={cancelReason}
							onChange={(event) => setCancelReason(event.target.value)}
							borderColor="gray.200"
						/>
					}
					continueText="Cancel"
					continueLoading={isLoading}
					onContinue={() => handleCancelClaim(cancelReason.trim()).then(() => resetModals(true))}
					continueDisabled={cancelReason.trim().length === 0}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isUncancelClaimModalOpen}
					size="3xl"
					message="Are you sure you want to un-cancel this claim?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleUncancelClaim().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isQueueClaimForPaymentModalOpen}
					size="3xl"
					message="Are you sure you want to queue this claim for payment?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleQueueClaimForPayment().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isClosePaidClaimModalOpen}
					size="3xl"
					message="Are you sure you want to close this claim as paid?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleClosePaidClaim().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isCloseNotPaidClaimModalOpen}
					size="3xl"
					message="Are you sure you want to close this claim as not paid?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleCloseNotPaidClaim().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isCloseNotPaidGoodwillModalOpen}
					size="3xl"
					message="Are you sure you want to mark this claim as Goodwill Payment? This action will change the status from Closed - Not Paid to Closed - Not Paid Goodwill."
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleCloseNotPaidGoodwillClaim().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isDuplicateClaimModalOpen}
					size="3xl"
					message="Are you sure you want to mark this claim as Duplicate?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handleDuplicateClaim().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isPendingProviderModalOpen}
					size="2xl"
					content={
						<>
							<Box justifyContent="space-between" display="flex" pt={3}>
								To queue this claim as pending provider review, please select the reasons:
							</Box>
							<FormControl pt={3} maxW="300px">
								<FormLabel>Reasons</FormLabel>
								<CheckboxGroup
									colorScheme="orange"
									onChange={(values) => setPendingProviderReasons(values)}
									value={pendingProviderReasons}>
									<Stack direction={"column"} pl={3}>
										<Checkbox value={ClaimsStatusReason.CLAIMS_STATUS_REASON_HOURLY_INVOICE.toString()}>
											Hourly Invoice
										</Checkbox>
										<Checkbox value={ClaimsStatusReason.CLAIMS_STATUS_REASON_FLAT_FEE_INVOICE.toString()}>
											Flat Fee Invoice
										</Checkbox>
										<Checkbox value={ClaimsStatusReason.CLAIMS_STATUS_REASON_SUPPORTING_DOCUMENTS.toString()}>
											Supporting Docs
										</Checkbox>
										<Checkbox value={ClaimsStatusReason.CLAIMS_STATUS_REASON_PROOF_OF_ENROLLMENT.toString()}>
											Proof of Enrollment
										</Checkbox>
										<Checkbox value={ClaimsStatusReason.CLAIMS_STATUS_REASON_OTHER.toString()}>Other</Checkbox>
									</Stack>
								</CheckboxGroup>
								{pendingProviderReasons.includes(ClaimsStatusReason.CLAIMS_STATUS_REASON_OTHER.toString()) && (
									<Input
										ml={8}
										mt={2}
										size="sm"
										value={pendingProviderOtherReason ?? ""}
										onChange={(event) => setPendingProviderOtherReason(event.target.value)}
									/>
								)}
							</FormControl>
							<FormControl pt={3} mb={5} maxW="300px">
								<FormLabel>Email the Attorney?</FormLabel>
								<Box pl={3}>
									<DetailField
										displayValue=""
										isEdit={true}
										variant="flushed"
										type="boolean"
										fontSize="1rem"
										maxW="100px"
										minW="80px"
										value={sendPendingProviderEmail ? 1 : 0}
										onNumberChange={(value) => setSendPendingProviderEmail(value === 1)}
										precision={2}
										pl={3}
									/>
								</Box>
							</FormControl>
						</>
					}
					continueText="Submit"
					continueDisabled={
						!pendingProviderReasons.length ||
						(pendingProviderReasons.includes(ClaimsStatusReason.CLAIMS_STATUS_REASON_OTHER.toString()) &&
							!pendingProviderOtherReason)
					}
					continueLoading={isLoading}
					onContinue={() =>
						handlePendingProvider(
							pendingProviderReasons,
							pendingProviderReasons.includes(ClaimsStatusReason.CLAIMS_STATUS_REASON_OTHER.toString())
								? pendingProviderOtherReason
								: undefined,
							sendPendingProviderEmail
						).then(() => resetModals(true))
					}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isPendingVerificationModalOpen}
					size="3xl"
					message="Are you sure you want to queue this claim as pending verification?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handlePendingVerification().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<CustomModal
					isOpen={isPendingInternalReviewModalOpen}
					size="3xl"
					message="Are you sure you want to queue this claim as pending internal review?"
					continueText="Confirm"
					continueLoading={isLoading}
					onContinue={() => handlePendingInternalReview().then(() => resetModals(true))}
					onCancel={() => resetModals()}
				/>
				<ServiceBillingEditModal
					isOpen={isServiceBillingEditModalOpen}
					onContinue={(servicesChanged) => {
						resetModals();

						const claimId = parseInt(id ?? "", 10);
						if (!claimId) return;
						if (!servicesChanged) {
							handleFetchClaim(claimId).then(() => handleFetchActivities(1));
							return;
						}

						// Since services have changed, generate the new claim forms and refresh the claim and activities
						handleGeneratePdf(false)
							.then(() => {
								handleFetchClaim(claimId);
								handleFetchClaimAlternate(claimId);
							})
							.then(() => handleFetchActivities(1));
					}}
					onClose={() => {
						resetModals();
					}}
				/>
				<BasicInfoEditModal
					isOpen={isBasicInfoEditModalOpen}
					onClose={() => resetModals()}
					onSubmit={() => {
						handleFetchClaim(parseInt(id ?? "")).then((data) => resetModals());
					}}
				/>
				<Box id="pageBody" width="100%" height="100%" overflow="auto">
					<Box mx={8}>
						<Page overflow="initial">
							{/* Header Section */}
							<HStack
								alignItems="center"
								margin="0 auto"
								position="sticky"
								top="0px"
								zIndex="2"
								background="white"
								marginTop="-15px"
								paddingTop="15px">
								<Button leftIcon={<HiChevronLeft />} variant="ghost" onClick={() => navigate("/claims")} mr="80px">
									Back
								</Button>
								<Heading fontSize="21px" fontWeight="700" color="gray.700" flex={1} textAlign="center">
									Claim {id ? "#" + id : ""} {claim?.caseId ? "for Case #" + claim?.caseId : ""}
									{claim?.atty ? (claim?.caseId ? ", " : "for ") : ""}
									{claim?.atty ? "Attorney #" + claim?.atty.getId() : ""}
								</Heading>
								<Box maxWidth="200px" display="flex" flexDirection="column" alignItems="flex-end">
									<Box display="flex" justifyContent="right" mb="2">
										<Menu variant="outline" gutter={0}>
											<MenuButton
												as={Button}
												rightIcon={<ChevronDownIcon fontSize="xl" marginRight="0" />}
												variant="outline"
												borderColor={"gray.500"}
												size="xs"
												fontSize="sm"
												fontWeight={500}
												paddingRight="5px"
												borderRadius="3px"
												isDisabled={
													claim?.isHeldForAudit ||
													// Disable status change button unless user has general edit permissions or has the Payments role and the claim is queued for payment
													(!hasGeneralUpdateAccess &&
														!(claim?.status === ClaimStatus.QueuedForPayment && roles.includes(UserRole.Payments)) &&
														!(claim?.status === ClaimStatus.Draft && roles.includes(UserRole.ClaimsAdmin)))
												}>
												{claim?.status}
											</MenuButton>
											<MenuList fontSize="14px" padding="0" borderRadius="2px" minWidth="120px">
												{getMenuList()}
											</MenuList>
										</Menu>
									</Box>
									<Select
										size="xs"
										borderColor={"gray.500"}
										_hover={{ borderColor: "gray.400" }}
										marginBottom="8px"
										borderRadius="3px"
										fontSize="sm"
										fontWeight={500}
										onChange={(event) => handleAssignAnalyst(+event.target.value)}
										isDisabled={!canEditAssignedAnalyst}
										value={
											Object.keys(claim?.assignedAnalyst ?? {}).length !== 0
												? claim?.assignedAnalyst?.getId().toString() ?? ""
												: "None"
										}>
										{(claim?.assignedAnalyst == null || Object.keys(claim?.assignedAnalyst).length === 0) && (
											<option key={""} value={"None"}>
												None
											</option>
										)}
										{getAnalystOptions()}
									</Select>
									{claim && hasGeneralManagerAccess && (
										<FormControl display="flex" alignItems="center" justifyContent="flex-end" mb="8px">
											<FormLabel htmlFor="hold-for-audit" mb="0" fontSize="sm" fontWeight={500}>
												Hold For Audit
											</FormLabel>
											<Switch
												id="hold-for-audit"
												colorScheme="orange"
												size="sm"
												isChecked={claim.isHeldForAudit === true}
												isDisabled={!hasGeneralManagerAccess || isLoading}
												onChange={handleAuditHoldToggle}
											/>
										</FormControl>
									)}
									{claim?.isHeldForAudit && <Box marginBottom="8px">{getAuditBadge()}</Box>}
									{claim?.dueDate && (
										<Box fontSize="sm" fontWeight={500} color="gray.700" textAlign="right" marginBottom="8px">
											Due: {new Date(claim.dueDate).toLocaleDateString()}
										</Box>
									)}
									{claim?.status === ClaimStatus.Draft && (
										<Button
											size="sm"
											colorScheme="orange"
											padding={2}
											onClick={() =>
												navigate(
													generatePath(RouterPathMapping.ClaimIntake, {
														id: claim.claimId?.toString() ?? null
													})
												)
											}
											isDisabled={!hasGeneralAdminAccess}>
											Continue
										</Button>
									)}
								</Box>
							</HStack>

							{/* Tab Navigation */}
							<Tabs colorScheme="orange" align="center" width="100%" margin="5px auto">
								<TabList
									display="flex"
									justifyContent="center"
									gap="22px"
									borderColor="chakra-border-color"
									borderBottomWidth="2px"
									borderBottomStyle="solid"
									position="sticky"
									top="80px"
									background="white"
									zIndex="1">
									{["Details", "Policy", "Payments", "Reports", "Notes"].map((tab) => (
										<Tab
											key={tab}
											fontSize="18px"
											fontWeight="400"
											lineHeight="normal"
											textAlign="center"
											color="gray.700"
											mb="-2px"
											position="relative"
											_selected={{
												color: "orange.600", // Selected color
												borderBottom: "2px solid",
												borderBottomColor: "orange.600"
											}}>
											{tab}
										</Tab>
									))}
								</TabList>

								<TabPanels>
									<TabPanel p={0} pt={4}>
										<VStack spacing={5} align="stretch">
											{/* Checklist Section */}
											{claim && ChecklistStatuses.indexOf(claim.status) > -1 && (
												<Box>
													<SectionHeader title="Checklist" width="100%" />
													<Box bg="offWhite" borderRadius="5px" px={4} py={2}>
														<Checklist />
													</Box>
												</Box>
											)}
											{/* Claim Information Section */}
											<Box>
												<SectionHeader
													title="Claim Information"
													width="100%"
													showEditButton={
														hasGeneralUpdateAccess &&
														claim?.status &&
														[
															ClaimStatus.InReview,
															ClaimStatus.PendingInternalReview,
															ClaimStatus.PendingProvider,
															ClaimStatus.PendingVerification
														].includes(claim.status)
													}
													onEditButtonClick={() => setIsBasicInfoEditModalOpen(true)}
												/>
												<Skeleton isLoaded={!!claim} endColor="gray.200">
													<DataCardDisplay
														data={getClaimInformation()}
														style={{ width: "100%" }}
														leftLabelMinWidth={100}
														rightLabelMinWidth={210}
													/>
												</Skeleton>
											</Box>

											{/* Services & Billing Section */}
											<Box>
												<SectionHeader
													title="Services & Billing"
													width="100%"
													showEditButton={
														hasGeneralUpdateAccess &&
														claim?.status &&
														[
															ClaimStatus.InReview,
															ClaimStatus.PendingInternalReview,
															ClaimStatus.PendingProvider,
															ClaimStatus.PendingVerification
														].includes(claim.status)
													}
													onEditButtonClick={() => setIsServiceBillingEditModalOpen(true)}
												/>
												<Skeleton isLoaded={!!claim} endColor="gray.200">
													<DataCardDisplayStatement data={claimDisplayStatementData} style={{ width: "100%" }} />
												</Skeleton>
											</Box>

											{/* Documents Section */}
											<Box mt="32px">
												<SectionHeader
													title={"Documents"}
													width="100%"
													showEditButton={
														hasGeneralUpdateAccess &&
														claim?.status &&
														[
															ClaimStatus.Draft,
															ClaimStatus.InReview,
															ClaimStatus.PendingInternalReview,
															ClaimStatus.PendingProvider,
															ClaimStatus.PendingVerification
														].includes(claim.status)
													}
													onEditButtonClick={() => {
														if (docEditButtonRef.current) {
															docEditButtonRef.current.click();
														}
													}}
												/>
												{claim && (
													<DataCardDisplayDocs
														showEditButton={!hasGeneralUpdateAccess}
														useExternalEditButton={true}
														buttonRef={docEditButtonRef}
														isPermanentDeleteEnabled={false}
														isProvider={isProvider}
													/>
												)}
											</Box>
										</VStack>
									</TabPanel>
									<TabPanel>
										{/* Policy Content */}
										<Box>Policy content goes here...</Box>
									</TabPanel>
									<TabPanel>{claim && claim.caseId && <ClaimPaymentsTable caseId={claim?.caseId} />}</TabPanel>
									<TabPanel>{claim?.claimId && <ClaimReportsTable claimId={claim.claimId} />}</TabPanel>
									<TabPanel>
										{/* Notes Content-> pass claimID for  */}
										<Notes />
									</TabPanel>
								</TabPanels>
							</Tabs>
						</Page>
					</Box>
				</Box>
				<ActivityPane />
			</HStack>
		</LayoutWrapper>
	);
};

export default ClaimsDetail;
